/**
 * Intestazione «hero» della scheda ticket v4 (`Centro Ticket v4.dc.html`
 * L322-L393, L1003-L1055; novita N16, N17, N20, N21).
 *
 * Montata SOLO da `v3/TicketHeaderCard.tsx` nel ramo `look === 'v4'`, con gli
 * stessi dati gia calcolati: stato dell'oggetto in modifica, menu, presence e
 * la resa dei bottoni (`renderAction`, che porta `aria-disabled`, il motivo
 * per lo screen reader e `busy`) arrivano dal chiamante. Qui nessuna logica
 * nuova: solo il markup del design.
 *
 * - Riga sopra la card: «Torna alla coda» a sinistra, presence e «sta
 *   scrivendo…» a destra (L323-L333).
 * - Card colorata col tono del canale mescolato al verde scurissimo
 *   (`--s4-ch-N-hero`, L336/L1005), testo bianco: pillole canale / numero /
 *   priorita / SLA / origine (L338-L343), oggetto H2 19px con matita
 *   (L347-L359), meta (L360), azioni (L362-L384), stepper (L386-L393).
 * - Menu ⋯ a gruppi con intestazione (L371-L382): `groupOverflow`.
 */
import React, { useId } from 'react';
import { ArrowLeft, MoreHorizontal, Pencil } from 'lucide-react';
import { S3Avatar, S3Badge, S3Menu, S3MenuItem } from '../v3/primitives';
import { originVisual, priorityVisual, type S3SlaBadge } from '../v3/visual';
import { groupOverflow, type S3Action, type S3ActionBar, type S3ActionId } from '../v3/actionsModel';
import { formatDate, initialsOf } from '../constants';
import { displayStatus } from '../displayStatus';
import { channelTone } from './channelPalette';
import ChannelSwatch from './ChannelSwatch';
import { hasChannelAvatar } from '../channelAvatar';
import StatusStepper from './StatusStepper';
import PriorityBars from './PriorityBars';
import ShareTicketButton, { type TicketPrintConfig } from '../ShareTicketButton';
import type { ShareLinksPayload, ShareLinksResult, TicketDetail } from '../../../services/supportApi';

export interface HeroViewer {
  repId: string;
  name: string;
  photo?: string;
}

export interface HeroSubjectEdit {
  /** Matita visibile (capability + ticket non chiuso). */
  editable: boolean;
  /** Bozza in modifica; `null` = non in modifica. */
  draft: string | null;
  setDraft: (v: string | null) => void;
  save: () => void;
  saving: boolean;
  valid: boolean;
  maxLength: number;
}

export interface HeroMenu {
  open: boolean;
  setOpen: (fn: (v: boolean) => boolean) => void;
  close: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}

export interface TicketHeroProps {
  ticket: TicketDetail;
  bar: S3ActionBar;
  sla: S3SlaBadge | null;
  onAction: (id: S3ActionId) => void;
  busy?: boolean;
  showBack: boolean;
  onBack: () => void;
  backLabel: string;
  requesterName: string;
  assegnati: string[];
  viewersConFoto: HeroViewer[];
  viewersSenzaFoto: HeroViewer[];
  typingName: string | null;
  subject: HeroSubjectEdit;
  menu: HeroMenu;
  icons: Record<S3ActionId, React.ReactNode>;
  /** Stessa resa dei bottoni del v3 (aria-disabled, motivo, busy). */
  renderAction: (a: S3Action, primary?: boolean) => React.ReactNode;
  /** Link firmati agli allegati per «Condividi via email». */
  createShareLinks?: (payload: ShareLinksPayload) => Promise<ShareLinksResult>;
  /** «Stampa» nel menu Condividi (adapter operatore). */
  print?: TicketPrintConfig;
}

const TicketHero: React.FC<TicketHeroProps> = ({
  ticket,
  bar,
  sla,
  onAction,
  busy,
  showBack,
  onBack,
  backLabel,
  requesterName,
  assegnati,
  viewersConFoto,
  viewersSenzaFoto,
  typingName,
  subject,
  menu,
  icons,
  renderAction,
  createShareLinks,
  print,
}) => {
  const uid = useId();
  const tone = channelTone(ticket.groupId);
  const pr = priorityVisual(ticket.priority);
  const or = originVisual(ticket.origin);
  const hasPresence = viewersConFoto.length + viewersSenzaFoto.length > 0;
  const groups = groupOverflow(bar.overflow);

  return (
    <div className="s4-ticket-top">
      {(showBack || hasPresence || typingName) && (
        <div className="s4-ticket-top__row">
          {showBack && (
            <button type="button" className="s3-link s4-ticket-top__back" onClick={onBack}>
              <ArrowLeft size={13} /> {backLabel}
            </button>
          )}
          <span className="s4-ticket-top__spacer" />
          {hasPresence && (
            <S3Badge tone="warn" small>
              {/* Come nel v3: chi ha la foto e nell'avatar (nome nel `title`),
                  chi non ce l'ha resta nell'elenco dei nomi. */}
              Anche qui:{' '}
              {viewersConFoto.map((v) => (
                <S3Avatar key={v.repId} initials={initialsOf(v.name)} size={18} src={v.photo} title={v.name} />
              ))}
              {viewersSenzaFoto.map((v) => v.name).join(', ')}
            </S3Badge>
          )}
          {typingName && (
            <span className="s3-typing">
              <Pencil size={12} aria-hidden="true" /> {typingName} sta scrivendo…
            </span>
          )}
        </div>
      )}

      <div
        className="s4-hero"
        style={{ ['--s4-hero-bg' as string]: `var(${tone.cssVar}-hero)` } as React.CSSProperties}
      >
        <span className="s4-hero__glow" aria-hidden="true" />
        <div className="s4-hero__pills">
          {hasChannelAvatar(ticket.groupId) && <ChannelSwatch channel={ticket.groupId} size={22} sigla />}
          <span className="s4-hero__chip" title={ticket.groupId?.nome || undefined}>{ticket.groupId?.nome || '—'}</span>
          <span className="s3-thead__num s4-hero__num">#{ticket.ticketNumber}</span>
          <span className="s4-hero__pill">
            <PriorityBars priority={ticket.priority} variant="hero" decorative />
            PRIORITÀ {pr.label}
          </span>
          {sla && (
            <span className={`s4-hero__pill s4-hero__pill--${sla.tone}`} title={sla.title}>
              {sla.label}
            </span>
          )}
          {or.label && <span className="s4-hero__pill">{or.label}</span>}
        </div>

        <div className="s4-hero__top">
          <div className="s4-hero__main">
            {subject.draft !== null ? (
              <div className="s4-hero__subject-edit">
                <input
                  type="text"
                  className="s4-hero__input"
                  aria-label="Oggetto del ticket"
                  value={subject.draft}
                  maxLength={subject.maxLength}
                  disabled={subject.saving}
                  autoFocus
                  onChange={(e) => subject.setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      subject.save();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      subject.setDraft(null);
                    }
                  }}
                />
                <button
                  type="button"
                  className="s3-pill s3-pill--sm s3-pill--primary"
                  disabled={subject.saving || !subject.valid}
                  onClick={() => subject.save()}
                >
                  Salva
                </button>
                <button
                  type="button"
                  className="s3-pill s3-pill--sm"
                  disabled={subject.saving}
                  onClick={() => subject.setDraft(null)}
                >
                  Annulla
                </button>
              </div>
            ) : (
              <div className="s4-hero__subject">
                <h2 className="s4-hero__h2">{ticket.subject}</h2>
                {subject.editable && (
                  <button
                    type="button"
                    className="s3-pill s3-pill--sm s3-pill--icon"
                    aria-label="Modifica oggetto"
                    title="Modifica oggetto"
                    disabled={busy}
                    onClick={() => subject.setDraft(ticket.subject || '')}
                  >
                    <Pencil size={11} aria-hidden="true" />
                  </button>
                )}
              </div>
            )}

            <div className="s4-hero__meta">
              <span>
                Richiedente <strong>{requesterName}</strong>
              </span>
              <span className="s4-hero__sep" aria-hidden="true">·</span>
              <span>
                Assegnato a <strong>{assegnati.length ? assegnati.join(', ') : 'nessuno'}</strong>
              </span>
              <span className="s4-hero__sep" aria-hidden="true">·</span>
              <span>Aperto il {formatDate(ticket.createdAt)}</span>
            </div>
          </div>

          <div className="s4-hero__actions">
            {bar.primary && renderAction(bar.primary, true)}
            {bar.secondary.map((a) => renderAction(a))}
            <ShareTicketButton ticket={ticket} className="s4-hero__btn" busy={busy} createShareLinks={createShareLinks} print={print} />
            {bar.overflow.length > 0 && (
              <>
                <button
                  ref={menu.triggerRef}
                  type="button"
                  className={`s3-pill s3-pill--icon${menu.open ? ' s3-pill--active' : ''}`}
                  title="Altre azioni"
                  aria-label="Altre azioni"
                  aria-haspopup="menu"
                  aria-expanded={menu.open}
                  aria-disabled={busy || undefined}
                  onClick={() => {
                    if (busy) return;
                    menu.setOpen((v) => !v);
                  }}
                >
                  <MoreHorizontal size={15} />
                </button>
                <S3Menu open={menu.open} onClose={menu.close} ariaLabel="Altre azioni" triggerRef={menu.triggerRef}>
                  {groups.map((g) => {
                    const hid = `${uid}-${g.group}`;
                    return (
                      <div key={g.group} role="group" aria-labelledby={hid} className="s4-menu__group">
                        <div id={hid} className="s4-menu__h">
                          {g.label}
                        </div>
                        {g.items.map((a) => (
                          <S3MenuItem
                            key={a.id}
                            danger={a.danger}
                            title={a.title}
                            aria-disabled={a.disabled || busy || undefined}
                            onClick={() => {
                              if (a.disabled || busy) return;
                              menu.close();
                              onAction(a.id);
                            }}
                          >
                            {icons[a.id]} {a.label}
                          </S3MenuItem>
                        ))}
                      </div>
                    );
                  })}
                </S3Menu>
              </>
            )}
          </div>
        </div>

        <StatusStepper
          status={ticket.status}
          closedLabel={displayStatus(ticket).derived ? displayStatus(ticket).label : undefined}
        />
      </div>
    </div>
  );
};

export default TicketHero;
